/**
 * @file SettingsService.ts
 * @role Owns the player's local settings (volumes, mute, turbo, graphics quality), persists them to
 *       local storage, and notifies subscribers on change. Persisted data is UNTRUSTED input
 *       (a user can edit local storage), so it is validated on load just like backend/config
 *       data — a corrupt blob falls back to defaults rather than poisoning the game.
 * @owns The settings state + persistence + change notifications.
 * @reads An injectable Storage (defaults to window.localStorage; absent in non-browser/test).
 * @neverDoes Decide game outcomes. Touch Pixi. Throw on bad stored data (defaults instead).
 * @invariants 10 (validate persisted data before use). Settings never affect the RGS outcome.
 * @see docs/CONFIG_GUIDE.md, src/scenes/SettingsScene/SettingsController.ts
 */
import { z } from 'zod';
import { noopLogger, type Logger } from '@core/logging/Logger';
import { GraphicsQualitySettingSchema } from '@core/graphics/GraphicsQualityService';

const unit = z.number().min(0).max(1);

export const SettingsSchema = z.object({
  musicVolume: unit,
  sfxVolume: unit,
  muted: z.boolean(),
  turbo: z.boolean(),
  graphicsQuality: GraphicsQualitySettingSchema.default('auto'),
});

export type Settings = z.infer<typeof SettingsSchema>;

export const DEFAULT_SETTINGS: Settings = {
  musicVolume: 0.7,
  sfxVolume: 0.8,
  muted: false,
  turbo: false,
  graphicsQuality: 'auto',
};

/** The slice of the Storage API we use; lets tests inject a fake and survives no-storage envs. */
export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): SettingsStorage | undefined {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
  } catch {
    return undefined; // localStorage can throw in sandboxed/private contexts
  }
}

type SettingsListener = (settings: Settings) => void;

export class SettingsService {
  private settings: Settings = { ...DEFAULT_SETTINGS };
  private readonly listeners = new Set<SettingsListener>();

  constructor(
    private readonly storage: SettingsStorage | undefined = defaultStorage(),
    private readonly storageKey = 'slot-game-1:settings',
    private readonly logger: Logger = noopLogger,
  ) {}

  /** Load + validate persisted settings. Invalid/absent/unreadable -> defaults (never throws). */
  load(): void {
    const raw = this.storage?.getItem(this.storageKey);
    if (!raw) return;
    try {
      const stored: unknown = JSON.parse(raw);
      const parsed = SettingsSchema.safeParse(stored);
      if (parsed.success) {
        this.settings = parsed.data;
        // Quick Spin used to be a persisted preference. It is now a request-scoped HUD action;
        // rewrite legacy blobs so a stale flag cannot influence this or a future runtime.
        if (isRecord(stored) && 'quickSpin' in stored) this.persist();
      } else {
        this.logger.warn('[settings] stored settings invalid; using defaults');
      }
    } catch {
      this.logger.warn('[settings] stored settings unreadable; using defaults');
    }
  }

  get(): Settings {
    return { ...this.settings };
  }

  /** Merge a partial change, persist, and notify. Invalid merged state is ignored (defensive). */
  update(patch: Partial<Settings>): void {
    const parsed = SettingsSchema.safeParse({ ...this.settings, ...patch });
    if (!parsed.success) return;
    this.settings = parsed.data;
    this.persist();
    this.notify();
  }

  /** Subscribe; the listener is called immediately with the current settings. Returns unsubscribe. */
  subscribe(listener: SettingsListener): () => void {
    this.listeners.add(listener);
    listener(this.get());
    return () => this.listeners.delete(listener);
  }

  private persist(): void {
    try {
      this.storage?.setItem(this.storageKey, JSON.stringify(this.settings));
    } catch {
      // A full or unavailable storage must not break gameplay; settings just won't persist.
    }
  }

  private notify(): void {
    const snapshot = this.get();
    for (const listener of [...this.listeners]) listener(snapshot);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
